import { type ComponentPropsWithoutRef, type ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import dayjs from 'dayjs';
import {
  BadgeStatus,
  Disclaimer,
  FormattedDate,
  FormattedMoney,
  MONEY_TOKENS,
} from '@repo/design-system-kit';
import { IconWarningSignFilled } from '@repo/monochrome-icons';
import { STATUS } from 'receivable-invoices-js/constants/invoices';
import { getInvoiceStatus } from 'receivable-invoices-js/react/utils/receivable-invoices';
import type { ReceivableInvoice } from 'receivable-invoices-js/react/api/models/receivable-invoice';
import styles from './information.module.css';

function SpanWrapper(parts: ReactNode[]) {
  return <span>{parts}</span>;
}

interface ReceivableInvoicesInformationProps extends ComponentPropsWithoutRef<'div'> {
  invoice: ReceivableInvoice;
}

export function ReceivableInvoicesInformation({
  invoice,
  className,
  ...props
}: ReceivableInvoicesInformationProps): ReactNode {
  const intl = useIntl();
  const { formatMessage, locale } = intl;

  // @ts-expect-error -- to update when migrating to React data model
  const isItalianOrganization = invoice.organization.legalCountry === 'IT';

  const formatDate = (value?: string) => {
    if (!value) return null;
    const date = dayjs(value);
    if (date.isValid()) {
      return <FormattedDate date={date.toDate().toISOString()} locale={locale} token="compact" />;
    }
    return null;
  };

  const getDaysLate = () => {
    if (!invoice.dueDate) return undefined;
    const dueDate = new Date(invoice.dueDate);
    if (dayjs(dueDate).isValid()) {
      return dayjs().startOf('day').diff(dayjs(dueDate).startOf('day'), 'day');
    }
    return undefined;
  };

  const isDraft = invoice.status === STATUS.DRAFT;
  const isPaid = invoice.status === STATUS.PAID;
  const isUnpaid = invoice.status === STATUS.UNPAID;
  const showPaymentDisclaimer = isUnpaid && !invoice.directDebitEnabled;

  const dueDate = formatDate(invoice.dueDate);
  const paidAtDate = formatDate(invoice.paidAt);
  const daysLate = getDaysLate();
  const status = getInvoiceStatus(invoice, intl);

  const disclaimer = isItalianOrganization
    ? formatMessage({ id: 'invoice.preview.draft.italy.disclaimer' })
    : formatMessage({ id: 'invoice.preview.draft.disclaimer' });

  return (
    <div className={cx(className)} {...props}>
      <BadgeStatus
        className="mb-16"
        data-test-invoice-information-status=""
        level={status.level}
        text={status.text}
      />

      <p className="title-3 mb-16" data-test-invoice-information-number>
        {!isDraft ? (
          <FormattedMessage
            id="invoice.preview.invoice-number"
            values={{ invoice_number: invoice.number }}
          />
        ) : (
          invoice.number
        )}
      </p>

      <div className={styles.price} data-test-invoice-information-price>
        <h1 className={cx(styles.priceValue, 'title-1')}>
          <FormattedMoney
            amount={invoice.amountDue.value}
            currency={invoice.currency}
            token={MONEY_TOKENS.ISO}
          />
        </h1>
        <span className={cx(styles.priceVat, 'caption')}>
          <FormattedMessage id="invoice.preview.include-vat" />
        </span>
      </div>

      <div className="mb-16">
        <p className="title-4" data-test-invoice-information-sent-by>
          <FormattedMessage
            id="invoice.preview.sent-by"
            values={{ companyName: invoice.organization.legalName }}
          />
        </p>
      </div>

      {isUnpaid && !invoice.overdue ? (
        <p className="body-1 mb-8" data-test-invoice-information-due-date>
          <FormattedMessage id="invoice.preview.pay-before" values={{ dueDate }} />
        </p>
      ) : null}

      {invoice.overdue ? (
        <p
          className={cx(styles.paymentLate, 'body-1', 'mb-8')}
          data-test-invoice-information-payment-late
        >
          <FormattedMessage id="invoice.preview.payment-late" values={{ count: daysLate }} />
          <IconWarningSignFilled className="ml-8" />
        </p>
      ) : null}

      {isPaid ? (
        <p className="body-1 mb-8" data-test-invoice-information-paid-at-date>
          <FormattedMessage id="invoice.preview.paid-at" values={{ payment_date: paidAtDate }} />
        </p>
      ) : null}

      {isDraft ? (
        <Disclaimer.Inline data-test-invoice-information-proforma-disclaimer="" level="info">
          <span>{disclaimer}</span>
        </Disclaimer.Inline>
      ) : null}

      {showPaymentDisclaimer ? (
        <Disclaimer.Inline
          className={styles.unpaidDisclaimer}
          data-test-invoice-information-unpaid-disclaimer=""
          level="info"
        >
          <span>
            <FormattedMessage
              id="invoice.preview.disclaimer"
              values={{
                invoice_number: invoice.number,
                payment_reference: invoice.reference,
                span: SpanWrapper,
              }}
            />
          </span>
        </Disclaimer.Inline>
      ) : null}
    </div>
  );
}
